import Vue from "vue";
import SortedTablePlugin from "vue-sorted-table";
import DatePicker from "vue2-datepicker";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { TH_SARABUN_TTF_BASE64 } from "@/assets/fonts/thsarabun.js";

Vue.use(SortedTablePlugin, {
  ascIcon: '<i class="mdi mdi-arrow-down"></i>',
  descIcon: '<i class="mdi mdi-arrow-up"></i>',
});

export default {
  components: {
    DatePicker,
  },
  data: function () {
    return {
      busy: false,
      busyExport: false,
      apiStatus: "กรุณากดปุ่มแสดงข้อมูลเพื่อค้นหาข้อมูล",
      items: [],
      summary: null,
      momentFormat: {
        stringify: (date) => (date ? moment(date).format("DD/MM/YYYY") : ""),
        parse: (value) => (value ? moment(value, "DD/MM/YYYY").toDate() : null),
      },
      // ตัวกรอง
      filterStart: new Date(moment().startOf("month").format("YYYY-MM-DD")),
      filterEnd: new Date(moment().endOf("month").format("YYYY-MM-DD")),
      filterDateBy: "cut", // cut = วันที่บัญชีตัดชำระ | customer = วันที่ลูกค้าชำระ
      filterStatus: "", // "" ทั้งหมด | 1..4 ตาม payments.status
      filterPaymentType: "", // "" ทั้งหมด | 0..4 วิธีจ่าย
      filterPayMode: "", // "" ทั้งหมด | full | partial
      filterKeyword: "",
    };
  },
  computed: {
    rows() {
      return this.items.length;
    },
  },
  methods: {
    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    statusVariant(text) {
      if (text === "อนุมัติการชำระ") return "text-success font-weight-bold";
      if (text === "ปฏิเสธการชำระ") return "text-danger font-weight-bold";
      if (text === "ยกเลิก") return "text-gray-400";
      return "text-warning font-weight-bold"; // รอตรวจสอบ
    },
    buildParams() {
      return {
        start_date: this.filterStart ? moment(this.filterStart).format("YYYY-MM-DD") : "",
        end_date: this.filterEnd ? moment(this.filterEnd).format("YYYY-MM-DD") : "",
        date_by: this.filterDateBy,
        status: this.filterStatus,
        payment_type: this.filterPaymentType,
        pay_mode: this.filterPayMode,
        keyword: this.filterKeyword || "",
      };
    },

    async onInitData() {
      try {
        this.items = [];
        this.busy = true;
        this.apiStatus = "กำลังค้นหาข้อมูล..";

        const res = await this.$axios.get(
          this.$host + "/api/v1/report/payment-receipt",
          { params: this.buildParams() }
        );

        this.busy = false;
        this.apiStatus = "ไม่พบข้อมูล";
        this.items = res.data.data || [];
        this.summary = res.data.summary;
      } catch (error) {
        this.busy = false;
        this.apiStatus = "ไม่พบข้อมูล";
        this.onExceptionHandler(
          (error.response && error.response.data && error.response.data.message) || "เกิดข้อผิดพลาด"
        );
      }
    },

    addThaiFont(doc) {
      if (!doc || !TH_SARABUN_TTF_BASE64) return;
      doc.addFileToVFS("THSarabun.ttf", TH_SARABUN_TTF_BASE64);
      doc.addFont("THSarabun.ttf", "THSarabun", "normal");
      doc.addFont("THSarabun.ttf", "THSarabun", "bold");
    },

    exportPDF() {
      if (this.busyExport) return;
      const exportRows = this.items;
      if (!exportRows.length) {
        this.$bvToast.toast("ไม่มีข้อมูลให้ส่งออก", { title: "Export PDF", variant: "warning", solid: true });
        return;
      }

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      this.addThaiFont(doc);
      doc.setFont("THSarabun", "normal");

      const margin = { top: 60, right: 24, bottom: 40, left: 24 };
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const title = "รายงานการรับชำระ";

      let pageNo = 1;
      const drawHeader = () => {
        doc.setFontSize(18);
        doc.setFont("THSarabun", "bold");
        doc.text(title, margin.left, 40);
      };
      const drawFooter = (n) => {
        doc.setFontSize(11);
        doc.text(`หน้า ${n}`, pageW - margin.right, pageH - 18, { align: "right" });
      };
      drawHeader();
      drawFooter(pageNo);

      const money = (n) => this.formatCurrency(n);
      const head = [[
        "#", "เลขที่ใบรับชำระ", "เลขที่ใบวางบิล", "เลขที่บิล", "วันที่ตัดชำระ",
        "วันที่ลูกค้าชำระ", "ชื่อลูกค้า", "ผู้ขาย", "มูลค่า", "วิธีจ่าย", "รูปแบบ", "ผู้ตัดชำระ", "สถานะ", "หมายเหตุ",
      ]];
      const body = exportRows.map((r) => [
        r.no,
        r.receipt_no || "-",
        r.billing_note_no || "-",
        r.bill_no || "-",
        r.cut_date || "-",
        r.customer_paid_date || "-",
        r.customer_name || "-",
        r.seller_name || "-",
        money(r.amount),
        r.payment_type_text || "-",
        r.pay_mode_text || "-",
        r.cut_by || "-",
        r.status_text || "-",
        r.remark || "-",
      ]);

      doc.autoTable({
        startY: margin.top,
        head,
        body,
        margin,
        pageBreak: "auto",
        styles: {
          font: "THSarabun", fontSize: 9, cellPadding: 2, overflow: "linebreak",
          lineColor: [220, 220, 220], lineWidth: 0.2, valign: "middle",
        },
        headStyles: { fillColor: [240, 240, 240], textColor: 20, halign: "center" },
        columnStyles: {
          0: { cellWidth: 20, halign: "right" },
          8: { halign: "right" },
        },
        didDrawPage: (data) => {
          if (data.pageNumber > pageNo) {
            pageNo = data.pageNumber;
            drawHeader();
            drawFooter(pageNo);
          }
        },
      });

      if (this.summary) {
        doc.autoTable({
          startY: (doc.lastAutoTable ? doc.lastAutoTable.finalY : margin.top) + 6,
          theme: "plain",
          margin,
          styles: { font: "THSarabun", fontSize: 12, cellPadding: 4 },
          body: [[
            "", "", "", "", "", "", "",
            `รวม ${this.summary.count.toLocaleString("th-TH")} รายการ`,
            money(this.summary.total_amount), "", "", "", "", "",
          ]],
        });
      }

      doc.save(`รายงาน_การรับชำระ_${new Date().toLocaleDateString("th-TH")}.pdf`);
    },

    exportExcel() {
      if (this.busyExport) return;
      const exportRows = this.items;
      if (!exportRows.length) {
        this.$bvToast.toast("ไม่มีข้อมูลให้ส่งออก", { title: "Export Excel", variant: "warning", solid: true });
        return;
      }

      // วันที่จาก backend เป็น string dd/mm/yyyy — แปลงเป็น Date object เพื่อให้ Excel มองเป็น type date
      const toDate = (s) => {
        if (!s) return null;
        const d = moment(s, "DD/MM/YYYY", true);
        return d.isValid() ? d.toDate() : null;
      };

      const rows = exportRows.map((r) => ({
        "ลำดับ": r.no,
        "เลขที่ใบรับชำระ": r.receipt_no || "-",
        "เลขที่ใบวางบิล": r.billing_note_no || "-",
        "เลขที่บิล": r.bill_no || "-",
        "วันที่บัญชีทำการตัดชำระ": toDate(r.cut_date) || "-",
        "วันที่ลูกค้าชำระ": toDate(r.customer_paid_date) || "-",
        "รหัสลูกค้า": r.customer_code || "-",
        "ชื่อลูกค้า": r.customer_name || "-",
        "ผู้ขาย": r.seller_name || "-",
        "มูลค่า": +parseFloat(r.amount || 0),
        "วิธีจ่าย": r.payment_type_text || "-",
        "รูปแบบ": r.pay_mode_text || "-",
        "ผู้ตัดชำระ": r.cut_by || "-",
        "สถานะการรับชำระ": r.status_text || "-",
        "สาขา": r.branch_name || "-",
        "หมายเหตุ": r.remark || "-",
      }));

      // cellDates: เก็บเป็น date cell จริง (t:'d') ไม่ใช่ serial number/string
      const ws = XLSX.utils.json_to_sheet(rows, { cellDates: true });
      ws["!cols"] = [
        { wch: 6 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 16 },
        { wch: 16 }, { wch: 10 }, { wch: 28 }, { wch: 18 }, { wch: 14 },
        { wch: 10 }, { wch: 16 }, { wch: 18 }, { wch: 16 }, { wch: 20 }, { wch: 20 },
      ];

      // format: "มูลค่า" (index 9) เป็นตัวเลข, วันที่ (index 4,5) เป็น dd/mm/yyyy
      const ref = XLSX.utils.decode_range(ws["!ref"]);
      for (let R = ref.s.r + 1; R <= ref.e.r; R++) {
        const cell = XLSX.utils.encode_cell({ r: R, c: 9 });
        if (ws[cell] && ws[cell].t === "n") ws[cell].z = "#,##0.00";
        for (const C of [4, 5]) {
          const dc = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[dc] && ws[dc].t === "d") ws[dc].z = "dd/mm/yyyy";
        }
      }

      if (this.summary) {
        const totalRow = ref.e.r + 2;
        ws[XLSX.utils.encode_cell({ r: totalRow, c: 8 })] = { t: "s", v: "รวมทั้งหมด" };
        ws[XLSX.utils.encode_cell({ r: totalRow, c: 9 })] = { t: "n", v: +this.summary.total_amount, z: "#,##0.00" };
      }

      ws["!autofilter"] = { ref: ws["!ref"] };
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "การรับชำระ");
      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(new Blob([wbout], { type: "application/octet-stream" }), `รายงาน_การรับชำระ_${new Date().toLocaleDateString("th-TH")}.xlsx`);
    },
  },
  mounted() {
    this.onInitData();
  },
};
